import { db } from '../config/firebase';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  query,
  where,
  writeBatch,
} from 'firebase/firestore';
import {
  FIRESTORE_BATCH_SIZE,
  FIRESTORE_IN_QUERY_LIMIT,
  IPOS_INVOICE_NOTIFICATIONS_COLLECTION,
} from '../constants/cukcuk';
import { InvoiceNotification } from '../types/invoiceNotification';

export const dedupeCukcukNotifications = async (
  notifications: InvoiceNotification[],
  documentIdFor: (notification: InvoiceNotification) => Promise<string>,
): Promise<Array<{ documentId: string; notification: InvoiceNotification }>> => {
  const rows = await Promise.all(notifications.map(async (notification) => {
    const documentId = await documentIdFor(notification);
    return { documentId, notification };
  }));
  return Array.from(new Map(rows.map((row) => [row.documentId, row])).values());
};

const removeUndefinedDeep = (value: any): any => {
  if (Array.isArray(value)) {
    return value.map(removeUndefinedDeep).filter(item => item !== undefined);
  }

  if (value && typeof value === 'object') {
    const cleanedEntries = Object.entries(value)
      .map(([key, nested]) => [key, removeUndefinedDeep(nested)] as const)
      .filter(([, nested]) => nested !== undefined);

    return Object.fromEntries(cleanedEntries);
  }

  return value === undefined ? undefined : value;
};

export class InvoiceNotificationService {
  constructor(
    private readonly collectionName: string = IPOS_INVOICE_NOTIFICATIONS_COLLECTION,
  ) {}

  getCollectionName(): string {
    return this.collectionName;
  }

  async addNotification(invoice: InvoiceNotification): Promise<InvoiceNotification> {
    try {
      const cleanedInvoice = removeUndefinedDeep(invoice);
      const docRef = await addDoc(collection(db, this.collectionName), {
        ...cleanedInvoice,
        createdAt: new Date(),
        updatedAt: new Date(),
      });

      return { id: docRef.id, ...cleanedInvoice };
    } catch (error) {
      throw new Error('Cannot add invoice notification: ' + error);
    }
  }

  async getAllNotifications(): Promise<InvoiceNotification[]> {
    try {
      const querySnapshot = await getDocs(collection(db, this.collectionName));
      const notifications = querySnapshot.docs.map(item => ({
        id: item.id,
        ...item.data(),
      })) as InvoiceNotification[];

      return notifications.sort((a, b) => {
        const aTime = (a.updatedAt as any)?.toDate?.()?.getTime?.() || 0;
        const bTime = (b.updatedAt as any)?.toDate?.()?.getTime?.() || 0;
        return bTime - aTime;
      });
    } catch (error) {
      throw new Error('Cannot retrieve invoice notifications: ' + error);
    }
  }

  async getNotificationsByStore(options: {
    storeUid?: string;
    storeAliases?: string[];
  }): Promise<InvoiceNotification[]> {
    const { storeUid, storeAliases = [] } = options;

    try {
      const tasks: Promise<any>[] = [];

      if (storeUid) {
        tasks.push(
          getDocs(
            query(
              collection(db, this.collectionName),
              where('storeUid', '==', storeUid),
            ),
          ),
        );
      }

      if (storeAliases.length > 0) {
        tasks.push(
          getDocs(
            query(
              collection(db, this.collectionName),
              where('store', 'in', storeAliases.slice(0, 10)),
            ),
          ),
        );
      }

      if (!tasks.length) {
        return [];
      }

      const snapshots = await Promise.all(tasks);
      const byId = new Map<string, InvoiceNotification>();

      snapshots.forEach((snapshot) => {
        snapshot.docs.forEach((item: any) => {
          const data = {
            id: item.id,
            ...item.data(),
          } as InvoiceNotification;
          byId.set(item.id, data);
        });
      });

      return Array.from(byId.values()).sort((a, b) => {
        const aTime = (a.updatedAt as any)?.toDate?.()?.getTime?.() || 0;
        const bTime = (b.updatedAt as any)?.toDate?.()?.getTime?.() || 0;
        return bTime - aTime;
      });
    } catch (error) {
      throw new Error('Cannot retrieve invoice notifications by store: ' + error);
    }
  }

  async updateNotification(id: string, data: Partial<InvoiceNotification>): Promise<void> {
    try {
      const cleanedData = removeUndefinedDeep(data);
      const docRef = doc(db, this.collectionName, id);
      await updateDoc(docRef, {
        ...cleanedData,
        updatedAt: new Date(),
      });
    } catch (error) {
      throw new Error('Cannot update invoice notification: ' + error);
    }
  }

  async deleteNotification(id: string): Promise<void> {
    try {
      const docRef = doc(db, this.collectionName, id);
      await deleteDoc(docRef);
    } catch (error) {
      throw new Error('Cannot delete invoice notification: ' + error);
    }
  }

  private static chunkValues<T>(values: T[], size: number): T[][] {
    const chunks: T[][] = [];
    for (let index = 0; index < values.length; index += size) {
      chunks.push(values.slice(index, index + size));
    }
    return chunks;
  }

  async getNotificationsByLineKeys(lineKeys: string[]): Promise<InvoiceNotification[]> {
    const uniqueKeys = Array.from(new Set(lineKeys.filter(Boolean)));
    if (!uniqueKeys.length) return [];

    const byId = new Map<string, InvoiceNotification>();

    for (const chunk of InvoiceNotificationService.chunkValues(uniqueKeys, FIRESTORE_IN_QUERY_LIMIT)) {
      const snapshot = await getDocs(
        query(collection(db, this.collectionName), where('lineKey', 'in', chunk)),
      );

      snapshot.docs.forEach((item) => {
        byId.set(item.id, { id: item.id, ...item.data() } as InvoiceNotification);
      });
    }

    return Array.from(byId.values());
  }

  async buildCukcukDocumentId(notification: InvoiceNotification): Promise<string> {
    if (typeof crypto === 'undefined' || !crypto.subtle?.digest || typeof TextEncoder === 'undefined') {
      throw new Error('Cannot build CUKCUK document ID: Web Crypto SHA-256 is unavailable');
    }
    const bytes = new TextEncoder().encode(this.buildCukcukUpsertKey(notification));
    const digest = await crypto.subtle.digest('SHA-256', bytes);
    return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
  }

  buildCukcukUpsertKey(notification: InvoiceNotification): string {
    if (notification.lineKey) return notification.lineKey;

    const lineIndex =
      typeof notification.rawData?.lineIndex === 'number'
        ? notification.rawData.lineIndex
        : '';

    return `${notification.invoiceCode}__${notification.itemId || notification.itemName || ''}__${lineIndex}`;
  }

  /**
   * Upsert CUKCUK invoice lines by lineKey (update existing, no fingerprint skip).
   */
  async batchUpsertCukcukNotifications(
    notifications: InvoiceNotification[],
  ): Promise<{ added: number; updated: number; skipped: number; errors: string[] }> {
    const result = {
      added: 0,
      updated: 0,
      skipped: 0,
      errors: [] as string[],
    };

    if (!notifications.length) return result;

    try {
      const deduped = await dedupeCukcukNotifications(
        notifications,
        this.buildCukcukDocumentId.bind(this),
      );
      result.skipped = notifications.length - deduped.length;
      const uniqueNotifications = deduped.map(({ notification }) => notification);
      const lineKeys = uniqueNotifications.map((item) => item.lineKey || '').filter(Boolean);
      const existingByLineKey = new Map<string, InvoiceNotification>();

      (await this.getNotificationsByLineKeys(lineKeys)).forEach((item) => {
        if (item.lineKey) existingByLineKey.set(item.lineKey, item);
      });

      const fallbackKeys = uniqueNotifications
        .filter((item) => !item.lineKey || !existingByLineKey.has(item.lineKey))
        .map((item) => this.buildCukcukUpsertKey(item));

      if (fallbackKeys.length) {
        const allExisting = await this.getAllNotifications();
        allExisting.forEach((item) => {
          const key = this.buildCukcukUpsertKey(item);
          if (fallbackKeys.includes(key) && !existingByLineKey.has(key)) {
            existingByLineKey.set(key, item);
          }
          if (item.lineKey) existingByLineKey.set(item.lineKey, item);
        });
      }

      const creates: InvoiceNotification[] = [];
      const updates: Array<{ id: string; data: InvoiceNotification }> = [];

      uniqueNotifications.forEach((notification) => {
        const lookupKey = notification.lineKey || this.buildCukcukUpsertKey(notification);
        const existed = notification.lineKey
          ? existingByLineKey.get(notification.lineKey) ?? existingByLineKey.get(lookupKey)
          : existingByLineKey.get(lookupKey);

        if (existed?.id) {
          updates.push({ id: existed.id, data: notification });
        } else {
          creates.push(notification);
        }
      });

      for (const chunk of InvoiceNotificationService.chunkValues(updates, FIRESTORE_BATCH_SIZE)) {
        const batch = writeBatch(db);
        chunk.forEach(({ id, data }) => {
          const cleanedData = removeUndefinedDeep(data);
          batch.update(doc(db, this.collectionName, id), {
            ...cleanedData,
            updatedAt: new Date(),
          });
        });

        try {
          await batch.commit();
          result.updated += chunk.length;
        } catch (error) {
          const message = error instanceof Error ? error.message : String(error);
          result.errors.push(`Batch update failed: ${message}`);
        }
      }

      for (const chunk of InvoiceNotificationService.chunkValues(creates, FIRESTORE_BATCH_SIZE)) {
        const batch = writeBatch(db);
        const createdRefs: Array<{ ref: ReturnType<typeof doc>; notification: InvoiceNotification }> = [];

        for (const notification of chunk) {
          const ref = doc(db, this.collectionName, await this.buildCukcukDocumentId(notification));
          const cleanedData = removeUndefinedDeep(notification);
          batch.set(ref, {
            ...cleanedData,
            createdAt: new Date(),
            updatedAt: new Date(),
          });
          createdRefs.push({ ref, notification });
        }

        try {
          await batch.commit();
          result.added += chunk.length;
          createdRefs.forEach(({ ref, notification }) => {
            const key = notification.lineKey || this.buildCukcukUpsertKey(notification);
            existingByLineKey.set(key, { ...notification, id: ref.id });
          });
        } catch (error) {
          const message = error instanceof Error ? error.message : String(error);
          result.errors.push(`Batch create failed: ${message}`);
        }
      }

      return result;
    } catch (error) {
      throw new Error('Batch upsert CUKCUK notifications failed: ' + error);
    }
  }

  async deleteAllNotifications(): Promise<number> {
    try {
      const querySnapshot = await getDocs(collection(db, this.collectionName));

      for (const document of querySnapshot.docs) {
        await deleteDoc(doc(db, this.collectionName, document.id));
      }

      return querySnapshot.size;
    } catch (error) {
      throw new Error('Cannot delete all invoice notifications: ' + error);
    }
  }

  async batchUpsertFromExcel(
    invoices: InvoiceNotification[]
  ): Promise<{ added: number; updated: number; skipped: number; errors: string[] }> {
    const result = {
      added: 0,
      updated: 0,
      skipped: 0,
      errors: [] as string[],
    };

    try {
      const existing = await this.getAllNotifications();

      // Build set of existing invoice fingerprints: orderCode (or invoiceCode) + checkInDate + checkInTime
      const existingInvoiceFingerprints = new Set<string>();
      const existingLineMap = new Map<string, InvoiceNotification>();

      existing.forEach(item => {
        const invoiceId = (item.orderCode || item.invoiceCode || '').trim();
        const checkInDate = (item.checkInDate || '').trim();
        const checkInTime = (item.checkInTime || '').trim();

        if (invoiceId && checkInDate) {
          existingInvoiceFingerprints.add(`${invoiceId}__${checkInDate}__${checkInTime}`);
        }

        if (item.lineKey) {
          existingLineMap.set(item.lineKey, item);
        } else if (item.invoiceCode) {
          existingLineMap.set(item.invoiceCode, item);
        }
      });

      // Group incoming invoices by their invoice fingerprint to track skipped invoices
      const skippedInvoiceKeys = new Set<string>();

      for (const invoice of invoices) {
        try {
          const invoiceId = (invoice.orderCode || invoice.invoiceCode || '').trim();
          const checkInDate = (invoice.checkInDate || '').trim();
          const checkInTime = (invoice.checkInTime || '').trim();
          const invoiceFingerprint = `${invoiceId}__${checkInDate}__${checkInTime}`;

          // Skip entire invoice if same invoiceCode + checkInDate + checkInTime already exists
          if (invoiceId && checkInDate && existingInvoiceFingerprints.has(invoiceFingerprint)) {
            if (!skippedInvoiceKeys.has(invoiceFingerprint)) {
              skippedInvoiceKeys.add(invoiceFingerprint);
            }
            result.skipped += 1;
            continue;
          }

          const upsertKey = invoice.lineKey || invoice.invoiceCode;
          const existed = existingLineMap.get(upsertKey);

          if (existed?.id) {
            await this.updateNotification(existed.id, invoice);
            result.updated += 1;
          } else {
            await this.addNotification(invoice);
            result.added += 1;
            if (upsertKey) {
              existingLineMap.set(upsertKey, { ...invoice, id: '__new__' });
            }
            // Register this new invoice's fingerprint to avoid duplicates within same upload batch
            if (invoiceId && checkInDate) {
              existingInvoiceFingerprints.add(invoiceFingerprint);
            }
          }
        } catch (error) {
          result.errors.push(
            `Lỗi xử lý hóa đơn ${invoice.invoiceCode}: ${error instanceof Error ? error.message : String(error)}`
          );
        }
      }

      return result;
    } catch (error) {
      throw new Error('Batch upsert invoice notifications failed: ' + error);
    }
  }
}
